import type { StateCreator } from 'zustand'
import type {
  ColumnDef,
  ModelRef,
  TableInventory,
  TableRow,
  TableSession,
} from '@modules/info-table/lib/tableSession'

// Estado da Tabela de Informação, tirado do `useState` da página para o
// store global. Motivo (bug do Rui): a casca do workspace desmonta a
// página do módulo sempre que se sai dela, e com o estado local isso
// deitava fora a tabela inteira - linhas, colunas, filtro, ordenação -
// obrigando a carregar tudo outra vez, o que em modelos grandes demora.
// Mesmo padrão do BoqSlice e do IfcEditSlice.
//
// O que NÃO vive aqui: estado efémero de interface da própria página
// (selector de colunas aberto, progresso da descoberta "Descobrir tudo",
// âncora do shift-click) e a lista de vistas gravadas, que é um espelho
// do localStorage e se relê ao montar.
//
// PERSISTÊNCIA (regra do beta.75): nada disto vai para localStorage. As
// linhas são derivadas do IFC, não são trabalho autoral: recarregam-se a
// partir do modelo em segundos e repô-las depois de um reload seria
// exactamente o "trabalho que reaparece sozinho" que a rede de
// recuperação proíbe. Só as vistas gravadas (SavedView, em
// `lib/tableViews.ts`) tocam no disco, e essas são preferência de
// apresentação, não trabalho - ver o comentário lá.

export type InfoTableStage =
  | 'idle'
  | 'inventorying'
  | 'discovering'
  | 'loading'
  | 'done'

export interface InfoTableSort {
  key: string
  dir: 'asc' | 'desc'
}

/** Chave do agrupamento "sem agrupamento". Vive aqui porque é o valor
 *  inicial do estado e o `tableViews` também o assume por omissão. */
export const INFO_TABLE_GROUP_NONE = '__none'

/**
 * Assinatura da federação de modelos que deu origem à tabela. Serve de
 * teste de validade: se a assinatura corrente não bater certo com a que
 * está no store, as linhas em memória são de outra combinação de IFCs e
 * têm de ir abaixo (é isto que impede linhas fantasma de um modelo já
 * descarregado).
 */
export function infoTableModelKey(models: ModelRef[]): string {
  return models.map((m) => `${m.modelId}\0${m.filename}`).join('\x01')
}

export interface InfoTableSlice {
  /** Leitor federado activo. Null enquanto não houver modelos. */
  infoTableSession: TableSession | null
  /** Assinatura dos modelos de `infoTableSession`. Ver `infoTableModelKey`. */
  infoTableModelKey: string
  /** IFCs escolhidos para a tabela. `null` = todos os carregados (o
   *  estado inicial), e assim um IFC novo entra sem ninguém reconciliar
   *  conjuntos. Depois de o utilizador desmarcar algum, passa a ser um
   *  conjunto explícito e um IFC novo já não entra sozinho: a tabela que
   *  ele tem carregada continua válida. */
  infoTableChosenModelIds: Set<string> | null
  infoTableStage: InfoTableStage
  infoTableError: string | null
  /** Aviso "os modelos mudaram e a tabela foi limpa". Ligado quando uma
   *  troca de modelos invalida linhas que já estavam carregadas. */
  infoTableStale: boolean
  infoTableInventory: TableInventory | null
  infoTableChosenClasses: Set<string>
  infoTableAvailableColumns: ColumnDef[]
  infoTableChosenColumns: ColumnDef[]
  infoTableRows: TableRow[]
  infoTableProgress: { done: number; total: number }
  infoTableFilterText: string
  infoTableSort: InfoTableSort | null
  infoTableGroupBy: string
  infoTableCollapsedGroups: Set<string>
  infoTableActiveViewId: string | null

  setInfoTableChosenModelIds: (ids: Set<string> | null) => void
  /** Adopta uma sessão nova (ou nenhuma) e limpa tudo o que era derivado
   *  dos modelos anteriores. Fecha a sessão que estiver a sair. */
  startInfoTableSession: (key: string, session: TableSession | null) => void
  setInfoTableStage: (s: InfoTableStage) => void
  setInfoTableError: (e: string | null) => void
  setInfoTableInventory: (inv: TableInventory | null) => void
  setInfoTableChosenClasses: (c: Set<string>) => void
  setInfoTableAvailableColumns: (c: ColumnDef[]) => void
  setInfoTableChosenColumns: (c: ColumnDef[]) => void
  setInfoTableRows: (r: TableRow[]) => void
  setInfoTableProgress: (p: { done: number; total: number }) => void
  setInfoTableFilterText: (t: string) => void
  setInfoTableSort: (s: InfoTableSort | null) => void
  setInfoTableGroupBy: (k: string) => void
  toggleInfoTableGroup: (key: string) => void
  expandInfoTableGroup: (key: string) => void
  setInfoTableActiveViewId: (id: string | null) => void
  /** Aplica uma vista gravada de uma vez (evita cinco `set` seguidos). */
  applyInfoTableView: (v: {
    id: string | null
    classes: string[]
    columns: ColumnDef[]
    sort: InfoTableSort | null
    groupBy: string
    filterText: string
  }) => void
  dismissInfoTableStale: () => void
  /** Lousa limpa. Chamado pelo "Fechar sessão". */
  resetInfoTable: () => void
}

/** Tudo o que é derivado dos modelos. Uma troca de IFCs leva isto. */
function derivedInitialState() {
  return {
    infoTableStage: 'idle' as InfoTableStage,
    infoTableError: null,
    infoTableInventory: null,
    infoTableChosenClasses: new Set<string>(),
    infoTableAvailableColumns: [] as ColumnDef[],
    infoTableChosenColumns: [] as ColumnDef[],
    infoTableRows: [] as TableRow[],
    infoTableProgress: { done: 0, total: 0 },
    infoTableFilterText: '',
    infoTableSort: null,
    infoTableGroupBy: INFO_TABLE_GROUP_NONE,
    infoTableCollapsedGroups: new Set<string>(),
    infoTableActiveViewId: null,
  }
}

export const createInfoTableSlice: StateCreator<InfoTableSlice> = (set) => ({
  infoTableSession: null,
  infoTableModelKey: '',
  infoTableChosenModelIds: null,
  infoTableStale: false,
  ...derivedInitialState(),

  setInfoTableChosenModelIds: (ids) => set({ infoTableChosenModelIds: ids }),

  startInfoTableSession: (key, session) =>
    set((s) => {
      if (s.infoTableSession && s.infoTableSession !== session) {
        try {
          s.infoTableSession.close()
        } catch {
          /* fechar é só largar o índice; nunca deve travar a troca */
        }
      }
      return {
        ...derivedInitialState(),
        infoTableSession: session,
        infoTableModelKey: key,
        // Só se avisa quem perdeu alguma coisa: sem linhas carregadas
        // não houve nada a invalidar. O aviso é pegajoso porque trocar
        // de IFC passa por descarregar o velho e carregar o novo: são
        // duas trocas de sessão seguidas, e só a primeira é que ainda vê
        // as linhas. Apaga-se ao carregar tabela nova ou ao dispensá-lo.
        infoTableStale: s.infoTableStale || s.infoTableRows.length > 0,
        infoTableStage: session ? 'inventorying' : 'idle',
      }
    }),

  setInfoTableStage: (v) => set({ infoTableStage: v }),
  setInfoTableError: (v) => set({ infoTableError: v }),
  setInfoTableInventory: (v) => set({ infoTableInventory: v }),
  setInfoTableChosenClasses: (v) => set({ infoTableChosenClasses: v }),
  setInfoTableAvailableColumns: (v) => set({ infoTableAvailableColumns: v }),
  setInfoTableChosenColumns: (v) => set({ infoTableChosenColumns: v }),
  // Tabela nova por cima: o aviso de invalidação já não tem assunto.
  setInfoTableRows: (v) =>
    set(v.length > 0 ? { infoTableRows: v, infoTableStale: false } : { infoTableRows: v }),
  setInfoTableProgress: (v) => set({ infoTableProgress: v }),
  setInfoTableFilterText: (v) => set({ infoTableFilterText: v }),
  setInfoTableSort: (v) => set({ infoTableSort: v }),
  setInfoTableGroupBy: (v) => set({ infoTableGroupBy: v }),

  toggleInfoTableGroup: (key) =>
    set((s) => {
      const next = new Set(s.infoTableCollapsedGroups)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return { infoTableCollapsedGroups: next }
    }),

  expandInfoTableGroup: (key) =>
    set((s) => {
      if (!s.infoTableCollapsedGroups.has(key)) return s
      const next = new Set(s.infoTableCollapsedGroups)
      next.delete(key)
      return { infoTableCollapsedGroups: next }
    }),

  setInfoTableActiveViewId: (id) => set({ infoTableActiveViewId: id }),

  applyInfoTableView: (v) =>
    set({
      infoTableChosenClasses: new Set(v.classes),
      infoTableChosenColumns: v.columns,
      infoTableSort: v.sort,
      infoTableGroupBy: v.groupBy,
      infoTableFilterText: v.filterText,
      infoTableActiveViewId: v.id,
    }),

  dismissInfoTableStale: () => set({ infoTableStale: false }),

  resetInfoTable: () =>
    set((s) => {
      if (s.infoTableSession) {
        try {
          s.infoTableSession.close()
        } catch {
          /* ignorar */
        }
      }
      return {
        ...derivedInitialState(),
        infoTableSession: null,
        infoTableModelKey: '',
        infoTableChosenModelIds: null,
        infoTableStale: false,
      }
    }),
})
